/**
 * Admin — Surveys
 *
 * Three tabs: compose/manage, read results, ask the data.
 *
 * No auth gate here: routes/admin.tsx already gates the whole /admin subtree on
 * super_admin, and RLS + the RPCs re-check server-side. This route is deliberately
 * NOT marked `support: true` in ADMIN_NAV — member sentiment is not a support
 * surface.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Copy, Loader2, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateWrittenShort } from '@/lib/dates';
import { toDateKey } from '@/lib/dates';
import { getTierConfig } from '@/lib/tier-config';
import { SurveyComposer } from '@/components/admin/surveys/SurveyComposer';
import { SurveyResults } from '@/components/admin/surveys/SurveyResults';
import { SurveyAsk } from '@/components/admin/surveys/SurveyAsk';
import {
  composeAttach,
  createSurvey,
  fetchSurveyResponses,
  cloneSurvey,
  deleteSurvey,
  fetchSurveyResponseCount,
  fetchSurveys,
  fetchThreadKeyForSurvey,
  updateSurvey,
  type SurveyInput,
  type SurveyRow,
} from '@/api/surveys';

const STATUS_CLASS: Record<SurveyRow['status'], string> = {
  draft: 'text-muted-foreground bg-muted/60 border-border',
  active: 'text-success bg-success/10 border-success/20',
  closed: 'text-muted-foreground bg-muted/40 border-border',
};

function SurveysPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState<'new' | 'edit' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // 30-day window for the Ask tab's grounding bundle, matching the analytics
  // screen's default.
  const { from, to } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return { from: toDateKey(start), to: toDateKey(now) };
  }, []);

  const surveysQuery = useQuery({ queryKey: ['surveys'], queryFn: fetchSurveys });
  const surveys = surveysQuery.data ?? [];

  const selected = surveys.find((s) => s.id === selectedId) ?? null;

  // Response count gates question editing on a live survey (see SurveyComposer).
  const selectedResponses = useQuery({
    queryKey: ['survey-responses', selected?.id],
    queryFn: () => fetchSurveyResponses(selected!.id),
    enabled: !!selected && composing === 'edit',
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return surveys;
    return surveys.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.title.toLowerCase().includes(q) ||
        s.status.toLowerCase().includes(q),
    );
  }, [surveys, search]);

  /**
   * The composition thread key for whatever the composer is editing.
   *
   * Owned here rather than in the composer because the thread has to be
   * ATTACHED to the survey after a successful save, and only this component
   * sees the saved row. A new survey gets a fresh key; an existing one has its
   * key looked up, because threads are keyed by thread_key (they start before
   * the survey row exists) and survey_id is only backfilled on save.
   */
  const [newThreadKey, setNewThreadKey] = useState(() => crypto.randomUUID());

  const existingThreadKey = useQuery({
    queryKey: ['survey-thread-key', selected?.id],
    queryFn: () => fetchThreadKeyForSurvey(selected!.id),
    enabled: composing === 'edit' && !!selected?.id,
    refetchOnWindowFocus: false,
  });

  const threadKey =
    composing === 'edit'
      ? // Undefined while the lookup is in flight — the composer hides the panel
        // rather than briefly mounting it against a key that is about to change.
        existingThreadKey.isPending
        ? undefined
        : (existingThreadKey.data ?? newThreadKey)
      : newThreadKey;

  const save = useMutation({
    mutationFn: (input: SurveyInput) =>
      composing === 'edit' && selected ? updateSurvey(selected.id, input) : createSurvey(input),
    onSuccess: (row) => {
      // Backfill survey_id onto the thread. Fire-and-forget: a survey that saved
      // is saved, and an orphaned thread costs the analytics context its
      // rationales, never the survey itself.
      if (threadKey) void composeAttach(threadKey, row.id).catch(() => {});
      // A fresh key for the next composition, so two surveys created back to
      // back cannot share a conversation.
      setNewThreadKey(crypto.randomUUID());
      setComposing(null);
      setSaveError(null);
      setSelectedId(row.id);
      qc.invalidateQueries({ queryKey: ['surveys'] });
    },
    onError: (e) => setSaveError(e instanceof Error ? e.message : 'Could not save.'),
  });

  /**
   * Clone — the weekly survey without rebuilding it.
   *
   * Server-side (rpc_clone_survey), because SurveyInput carries no rest_period,
   * snooze_duration or max_shows: cloning through the save payload would reset
   * all three to DB defaults, and a 6-month rest floor on a weekly survey looks
   * exactly like the survey quietly breaking.
   *
   * The clone lands as a DRAFT and is selected, so the admin sees the new date
   * and the audience before anything is claimed. It keeps the SAME event, so on
   * a weekly survey the event still points at last week's session — the composer
   * says so rather than silently re-asking the wrong people.
   */
  const clone = useMutation({
    mutationFn: (id: string) => cloneSurvey(id),
    onSuccess: async (newId) => {
      setSaveError(null);
      await qc.invalidateQueries({ queryKey: ['surveys'] });
      setSelectedId(newId);
    },
    onError: (e) => setSaveError(e instanceof Error ? e.message : 'Could not clone.'),
  });

  /**
   * Delete — the survey and everything filed under it.
   *
   * One DELETE on `surveys` is the whole operation: survey_responses,
   * survey_invites and survey_intent_messages all cascade, and
   * `surveys_admin_all` already grants super_admins ALL on the table. No RPC.
   *
   * The confirmation fetches the response count first, because that is the part
   * that cannot come back. An anonymous answer has no link to a person, so
   * there is nothing left to reconstruct it from — the number has to be in front
   * of the admin BEFORE they press the button, not in a toast afterwards.
   */
  const [deleteTarget, setDeleteTarget] = useState<SurveyRow | null>(null);

  const deleteImpact = useQuery({
    queryKey: ['survey-response-count', deleteTarget?.id],
    queryFn: () => fetchSurveyResponseCount(deleteTarget!.id),
    enabled: !!deleteTarget,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSurvey(id),
    onSuccess: async (_void, id) => {
      setSaveError(null);
      setDeleteTarget(null);
      // The deleted survey may be the one on screen.
      if (selectedId === id) setSelectedId(null);
      await qc.invalidateQueries({ queryKey: ['surveys'] });
    },
    onError: (e) => setSaveError(e instanceof Error ? e.message : 'Could not delete.'),
  });

  return (
    // The admin shell wraps <Outlet /> in `flex-1 min-h-0 overflow-hidden`, so each
    // page owns its own scroller — without this the composer and a long response
    // list are clipped, not scrollable. Matches admin/analytics.tsx:259.
    // The bottom pad clears the iPhone home indicator: the admin shell has no
    // safe-area handling of its own, so the last row would sit under it in the PWA.
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-7xl space-y-4 p-4 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:p-6 sm:pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-xl font-bold text-foreground">Surveys</h1>
            <p className="text-xs text-muted-foreground">
              Ask a slice of the membership a short question, in-app.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => surveysQuery.refetch()}
          aria-label="Refresh"
          className="flex min-h-[36px] items-center rounded-lg border border-border px-2.5 text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className={`h-4 w-4 ${surveysQuery.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <Tabs defaultValue="surveys" className="space-y-4">
        <TabsList>
          <TabsTrigger value="surveys">Surveys</TabsTrigger>
          <TabsTrigger value="responses">Responses</TabsTrigger>
          <TabsTrigger value="ask">Ask the data</TabsTrigger>
        </TabsList>

        {/* ─── Surveys ──────────────────────────────────────────── */}
        <TabsContent value="surveys" className="space-y-4">
          {composing ? (
            <SurveyComposer
              survey={composing === 'edit' && selected ? selected : undefined}
              responseCount={selectedResponses.data?.length ?? 0}
              saving={save.isPending}
              error={saveError}
              threadKey={threadKey}
              onSave={(input) => save.mutate(input)}
              onCancel={() => {
                setComposing(null);
                setSaveError(null);
              }}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    // Clears on focus so a new term can be typed straight away
                    // without selecting-and-deleting the old one first.
                    onFocus={() => setSearch('')}
                    placeholder="Search surveys by name, title or status"
                    className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-base sm:text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(null);
                    setComposing('new');
                  }}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground hover:opacity-90"
                >
                  <Plus className="h-4 w-4" /> New survey
                </button>
              </div>

              {surveysQuery.isLoading && <Skeleton className="h-32" />}
              {surveysQuery.isError && (
                <p className="text-sm text-danger">
                  {surveysQuery.error instanceof Error
                    ? surveysQuery.error.message
                    : 'Failed to load surveys.'}
                </p>
              )}
              {surveysQuery.data && filtered.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {surveys.length === 0 ? 'No surveys yet.' : 'Nothing matches that search.'}
                </p>
              )}

              <div className="space-y-2">
                {filtered.map((s) => (
                  <Card
                    key={s.id}
                    className={`cursor-pointer transition-colors hover:bg-muted/40 ${
                      selectedId === s.id ? 'ring-2 ring-primary/40' : ''
                    }`}
                    onClick={() => setSelectedId(s.id)}
                  >
                    <CardContent className="flex flex-wrap items-center gap-3 py-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-semibold text-foreground">{s.name}</p>
                          <span
                            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_CLASS[s.status]}`}
                          >
                            {s.status}
                          </span>
                          {s.is_anonymous && (
                            <span className="rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                              anonymous
                            </span>
                          )}
                          <button
                            type="button"
                            // stopPropagation: the whole Card is a select
                            // target, and cloning must not also re-select the
                            // SOURCE — onSuccess selects the new draft.
                            onClick={(e) => {
                              e.stopPropagation();
                              clone.mutate(s.id);
                            }}
                            disabled={clone.isPending}
                            title="Clone into a new draft — same questions, same audience, window moved forward"
                            className="ml-auto inline-flex min-h-[32px] items-center gap-1.5 rounded-lg border border-border px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                          >
                            {clone.isPending && clone.variables === s.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                            Clone
                          </button>
                          <button
                            type="button"
                            // stopPropagation for the same reason as Clone: the
                            // Card is a select target, and opening the confirm
                            // must not also switch the composer to this row.
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(s);
                            }}
                            disabled={remove.isPending}
                            title="Delete this survey and every answer filed under it"
                            aria-label={`Delete ${s.name}`}
                            className="inline-flex min-h-[32px] items-center gap-1.5 rounded-lg border border-border px-2 text-[11px] font-medium text-muted-foreground hover:bg-danger/10 hover:text-danger hover:border-danger/40 disabled:opacity-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.title}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                          <span>{s.questions.length} questions</span>
                          <span>
                            {s.placement === 'login' ? 'On login' : 'On entering community'}
                          </span>
                          <span>
                            {s.sample_every_nth
                              ? `every ${s.sample_every_nth}${s.sample_every_nth === 2 ? 'nd' : s.sample_every_nth === 3 ? 'rd' : 'th'} person`
                              : 'everyone'}
                          </span>
                          {/* Pacing, stated on every row rather than only inside
                              the composer. A survey meant for three people a week
                              once went to 55 because the only place the rate was
                              visible was two clicks away, and it showed the wrong
                              thing when unset. */}
                          <span
                            className={
                              // Amber only on a LIVE unpaced survey, which is
                              // consuming audience right now. Colouring every
                              // draft and every legitimately-unpaced survey
                              // teaches people to ignore the colour.
                              !s.invite_rate_count && s.status === 'active'
                                ? 'font-medium text-warning-badge'
                                : undefined
                            }
                          >
                            {s.invite_rate_count
                              ? `${s.invite_rate_count} per ${s.invite_rate_unit ?? 'week'}`
                              : 'no rate limit'}
                          </span>
                          <span>
                            {s.audience_tier_codes && s.audience_tier_codes.length > 0
                              ? s.audience_tier_codes
                                  .map((c) => getTierConfig(c).label)
                                  .join(', ')
                              : 'all tiers'}
                          </span>
                          {s.starts_at && <span>from {formatDateWrittenShort(s.starts_at)}</span>}
                          {s.ends_at && <span>to {formatDateWrittenShort(s.ends_at)}</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(s.id);
                          setComposing('edit');
                        }}
                        className="min-h-[36px] rounded-lg border border-border px-2.5 text-xs font-medium text-foreground hover:bg-muted"
                      >
                        Edit
                      </button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* ─── Responses ────────────────────────────────────────── */}
        <TabsContent value="responses" className="space-y-4">
          {!selected ? (
            <p className="text-sm text-muted-foreground">
              Pick a survey on the Surveys tab to see its responses.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground">{selected.name}</p>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_CLASS[selected.status]}`}
                >
                  {selected.status}
                </span>
              </div>
              <SurveyResults survey={selected} />
            </>
          )}
        </TabsContent>

        {/* ─── Ask ──────────────────────────────────────────────── */}
        <TabsContent value="ask">
          <SurveyAsk from={from} to={to} />
        </TabsContent>
      </Tabs>

      {save.isPending && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
        </div>
      )}

      {/* Delete confirmation. The answer count is fetched while this is open and
          named in the sentence, because it is the only part of a survey that
          cannot be rebuilt: an anonymous response has no link back to a person.
          The button stays disabled until that count has resolved, so nobody can
          confirm a deletion whose size they have not been shown. */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => !next && !remove.isPending && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteImpact.isPending ? (
                'Checking how many answers this would destroy…'
              ) : deleteImpact.isError ? (
                'Could not check how many answers this survey has. Deleting now would remove an unknown number of them.'
              ) : (deleteImpact.data ?? 0) > 0 ? (
                <>
                  This permanently deletes the survey and the{' '}
                  <strong className="text-foreground">
                    {deleteImpact.data} answer{deleteImpact.data === 1 ? '' : 's'}
                  </strong>{' '}
                  filed under it, along with its invitations. The answers are anonymous, so
                  there is nothing to reconstruct them from. This cannot be undone.
                </>
              ) : (
                'This permanently deletes the survey and its invitations. No one has answered it yet. This cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending || deleteImpact.isPending}
              onClick={(e) => {
                // Radix closes on action by default; the mutation owns closing
                // so the dialog cannot vanish before the delete has landed.
                e.preventDefault();
                if (deleteTarget) remove.mutate(deleteTarget.id);
              }}
              className="bg-danger text-white hover:bg-danger/90"
            >
              {remove.isPending ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete survey'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/surveys')({
  component: SurveysPage,
});
